import { Box, CardActions, styled, useTheme } from '@mui/material'
import { DeleteOutline, EditOutlined } from '@mui/icons-material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { convertToPascalCase } from 'src/utils/function'
import { DynamicTextTypography } from './DynamicTextTypography'
import { GridActionButton } from './common/GridActionButton'
import { ReactComponent as RovFolderIcon } from '../assets/svg/RovFolderIcon.svg'
import { useDialogCtx } from 'src/hooks/context-hooks'
import UpdateROVCategoryDialog from 'src/dialogs/rov/UpdateROVCategoryDialog'
import { IROVVideoCategory } from 'src/generated-types/rovvideo'
import { invalidatePositionCache, useDeletePositionROVCategory } from 'src/api/positions'
import { invalidateMainComponentCache, useDeleteMainComponentROVCategory } from 'src/api/main-component'
import { toast } from 'react-toastify'

interface Props {
  categoryName: string
  onClick: () => void
  count?: number
  category: IROVVideoCategory
  customerId?: EntityId | null
  localityId?: EntityId | null
  mooringId?: EntityId | null
  positionId?: EntityId | null
  mainComponentId?: EntityId | null
  disabled?: boolean
}

export const ROVFolder: React.FC<Props> = ({
  categoryName,
  onClick,
  count,
  category,
  customerId,
  localityId,
  mooringId,
  positionId,
  mainComponentId,
  disabled = false,
}) => {
  const { t } = useTranslation(['rov', 'common'])
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState(false)
  const { showDialog, showWarningDialog } = useDialogCtx()
  const { mutateAsync: deletePositionROVCategory, isLoading: isLoadingPosition } = useDeletePositionROVCategory(
    customerId!,
    localityId!,
    mooringId!,
  )
  const { mutateAsync: deleteMainComponentROVCategory, isLoading: isLoadingMainComponent } =
    useDeleteMainComponentROVCategory(customerId!)

  const handleEdit = React.useCallback(async () => {
    const added = await showDialog(
      UpdateROVCategoryDialog,
      {
        componentProps: {
          category,
          customerId,
          localityId,
          mooringId,
          positionId,
          mainComponentId,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        title: t('editCategory', { ns: 'rov' }),
      },
      undefined,
      false,
    )
    if (!added) return
  }, [showDialog, category, t, customerId, localityId, mooringId, positionId, mainComponentId])

  const handleDelete = React.useCallback(async () => {
    await showWarningDialog(
      t('areYouSureDeleteCategory', { ns: 'rov' }),
      t('deletingSharedCategory', { ns: 'rov' }),
      false,
      [
        {
          text: t('deleteCategoryWithVideos', { ns: 'rov' }),
          variant: 'outlined',
          sx: {
            color: theme.palette.errorMain[theme.palette.mode],
            borderColor: theme.palette.errorMain[theme.palette.mode],
            ':hover': {
              borderColor: theme.palette.errorMain[theme.palette.mode],
              bgcolor: 'transparent',
            },
            bgcolor: 'transparent',
            width: '200px',
          },
          callback: () => handleCategoryDelete(true),
        },
        {
          text: t('deleteCategoryOnly', { ns: 'rov' }),
          variant: 'contained',
          sx: {
            bgcolor: theme.palette.errorMain[theme.palette.mode],
            ':hover': { bgcolor: theme.palette.errorLight[theme.palette.mode] },
            width: '200px',
          },
          callback: () => handleCategoryDelete(false),
        },
      ],
    )
  }, [showDialog, category, t, customerId, localityId, mooringId, positionId, mainComponentId])

  const handleCategoryDelete = async (isDeleteWithVideos: boolean) => {
    positionId != null
      ? await deletePositionROVCategory(
          `${positionId}/${category.id}/rov-category` + `?deleteVideos=${isDeleteWithVideos}`,
        )
      : await deleteMainComponentROVCategory(
          `${mainComponentId}/${category.id}/rov-category` + `?deleteVideos=${isDeleteWithVideos}`,
        )
    invalidatePositionCache.getPositionROVVideos(customerId!, localityId!, mooringId as EntityId, undefined)
    invalidateMainComponentCache.getMainComponentROVVideos(customerId!, undefined)
    toast.success(t('deletedSuccessfully', { ns: 'common' }))
  }

  return (
    <Box sx={{ marginLeft: '4px', marginBottom: 1 }}>
      <ROVFolderBox onClick={onClick} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            paddingLeft: 1,
          }}
        >
          <RovFolderIcon />
          <Box sx={{ marginLeft: 1, display: 'flex', flexDirection: 'column' }}>
            <DynamicTextTypography
              lineHeight={1.5}
              fontWeight={500}
              fontSize={12}
              maxLength={16}
              text={convertToPascalCase(categoryName)}
            />
            <DynamicTextTypography
              lineHeight={1}
              fontWeight={500}
              fontSize={11}
              maxLength={14}
              color={theme.palette.secondary.main}
              text={`${count} ${count === 1 ? t('video') : t('videos')}`}
            />
          </Box>
        </Box>
        {isHovered && (
          <CardActions
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              borderRadius: 1,
              padding: '0px 0px',
            }}
          >
            <GridActionButton
              toolTipTitle={t('edit', { ns: 'common' })}
              width="24px"
              height="24px"
              buttonIcon={<EditOutlined sx={{ fontSize: '14px', color: 'white' }} />}
              onClick={e => {
                e.stopPropagation()
                handleEdit()
              }}
              disabled={disabled}
            />

            <GridActionButton
              toolTipTitle={t('delete', { ns: 'common' })}
              width="24px"
              height="24px"
              sx={{ marginLeft: '-2px !important' }}
              buttonIcon={
                <DeleteOutline
                  sx={{
                    fontSize: '14px',
                    color: 'white',
                  }}
                />
              }
              onClick={e => {
                e.stopPropagation()
                handleDelete()
              }}
              disabled={disabled}
            />
          </CardActions>
        )}
      </ROVFolderBox>
    </Box>
  )
}

const ROVFolderBox = styled(Box)(({ theme }) => ({
  width: 170,
  height: 50,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px 4px 4px 4px',
  border: '1px solid',
  borderColor: theme.palette.paleBlue.main,
  backgroundColor: theme.palette.backgroundMain.main,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
}))
